import { useEffect, useState, useCallback } from 'react';
import { KDSSSEClient, OrdersSSEClient } from '../services/sse/client';

interface SSEStatus {
  isConnected: boolean;
  error: string | null;
  lastUpdate: number | null;
}

// Hook for KDS real-time updates
export function useKDSSSE(branchId: string | undefined, onUpdate?: (data: any) => void) {
  const [status, setStatus] = useState<SSEStatus>({
    isConnected: false,
    error: null,
    lastUpdate: null,
  });

  useEffect(() => {
    if (!branchId) {
      return;
    }

    const client = new KDSSSEClient(branchId, {
      onOpen: () => {
        setStatus((prev) => ({ ...prev, isConnected: true, error: null }));
      },
      onError: () => {
        setStatus((prev) => ({
          ...prev,
          isConnected: false,
          error: 'Connection error',
        }));
      },
    });

    // Register update callback
    if (onUpdate) {
      client.onKDSUpdate((data) => {
        setStatus((prev) => ({ ...prev, lastUpdate: Date.now() }));
        onUpdate(data);
      });
    }

    // Connect
    client.connect();

    // Cleanup on unmount
    return () => {
      client.disconnect();
    };
  }, [branchId, onUpdate]);

  return status;
}

// Hook for order events
export function useOrdersSSE(
  branchId: string | undefined,
  onEvent?: (event: any) => void
) {
  const [status, setStatus] = useState<SSEStatus>({
    isConnected: false,
    error: null,
    lastUpdate: null,
  });

  useEffect(() => {
    if (!branchId) {
      return;
    }

    const client = new OrdersSSEClient(branchId, {
      onOpen: () => {
        setStatus((prev) => ({ ...prev, isConnected: true, error: null }));
      },
      onError: () => {
        setStatus((prev) => ({
          ...prev,
          isConnected: false,
          error: 'Connection error',
        }));
      },
    });

    // Register event callback
    if (onEvent) {
      client.onOrderEvent((event) => {
        setStatus((prev) => ({ ...prev, lastUpdate: Date.now() }));
        onEvent(event);
      });
    }

    client.connect();

    return () => {
      client.disconnect();
    };
  }, [branchId, onEvent]);

  return status;
}

// Generic SSE hook with reconnection logic
export function useSSEConnection(
  url: string | null,
  onMessage?: (event: MessageEvent) => void
) {
  const [isConnected, setIsConnected] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connect = useCallback(() => {
    if (!url) return null;

    const eventSource = new EventSource(url);

    eventSource.onopen = () => {
      console.log('SSE connected');
      setIsConnected(true);
      setError(null);
    };

    eventSource.onmessage = (event) => {
      if (onMessage) {
        onMessage(event);
      }
    };

    eventSource.onerror = () => {
      console.error('SSE error');
      setIsConnected(false);
      setError('Connection failed');
      eventSource.close();
    };

    return eventSource;
  }, [url, onMessage]);

  useEffect(() => {
    const eventSource = connect();

    return () => {
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [connect]);

  return { isConnected, error };
}
